import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';

// Types
interface MenuContextType {
  isMenuOpen: boolean;
  toggleMenu: () => void;
  openMenu: () => void;
  closeMenu: () => void;
}

interface MenuProviderProps {
  children: ReactNode;
}

// Create context with proper typing
const MenuContext = createContext<MenuContextType | undefined>(undefined);

// Custom hook with error handling
export const useMenu = (): MenuContextType => {
  const context = useContext(MenuContext);
  if (context === undefined) {
    throw new Error('useMenu must be used within a MenuProvider');
  }
  return context;
};

// MenuProvider component with optimizations
export const MenuProvider: React.FC<MenuProviderProps> = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Memoized menu handlers with error handling
  const toggleMenu = useCallback(() => {
    try {
      setIsMenuOpen(prev => !prev);
    } catch (error) {
      console.error('MenuContext: Error toggling menu:', error);
    }
  }, []);

  const openMenu = useCallback(() => {
    try {
      setIsMenuOpen(true);
    } catch (error) {
      console.error('MenuContext: Error opening menu:', error);
    }
  }, []);

  const closeMenu = useCallback(() => {
    try {
      setIsMenuOpen(false);
    } catch (error) {
      console.error('MenuContext: Error closing menu:', error);
    }
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    isMenuOpen,
    toggleMenu,
    openMenu,
    closeMenu
  }), [isMenuOpen, toggleMenu, openMenu, closeMenu]);

  return (
    <MenuContext.Provider value={contextValue}>
      {children}
    </MenuContext.Provider>
  );
}; 